import { ChatListItem, getChatUrl, openAuthedChatUrl } from '@lib/network/chats';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import StarIcon from '@mui/icons-material/Star';
import {
  Button,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { chatListStore } from '@stores/ChatList';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import React from 'react';
import { DeleteChatModal, DeleteChatModalRef } from './DeleteChatModal';
import css from './RecentChatsSection.module.scss';
import { RenameChatModal, RenameChatModalRef } from './RenameChatModal';

interface Props {
  chat: ChatListItem;
  /** Called just before a star toggle, with the chat's *next* starred state, so
   *  the parent can expand the group the chat is about to move into (starring →
   *  Starred, unstarring → Recents). Prevents the moved row from vanishing into a
   *  collapsed destination group. */
  onStarToggle?: (willBeStarred: boolean) => void;
  /** Called on chat-row tap; the mobile overlay closes itself here.
   *  openAuthedChatUrl is async and toasts (without navigating) on a
   *  failed token exchange, so the overlay can't rely on page unload. */
  onNavigate?: () => void;
}

export const ChatMenuItem = observer(function ChatMenuItem({
  chat,
  onStarToggle,
  onNavigate,
}: Props) {
  const [menuAnchor, setMenuAnchor] = React.useState<HTMLElement | null>(null);
  const renameModalRef = React.useRef<RenameChatModalRef | null>(null);
  const deleteModalRef = React.useRef<DeleteChatModalRef | null>(null);

  const title = chat.title || 'Untitled';

  const openChat = () => {
    const url = getChatUrl(chat._id);
    if (!url) return;
    onNavigate?.();
    void openAuthedChatUrl(url, '_self', {
      surface: 'sidebar',
      control: 'recent-chats-row',
      chatId: chat._id,
    });
  };

  const onMenuButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  return (
    <>
      <Tooltip title={menuAnchor ? '' : title} arrow placement="right" enterDelay={300}>
        <Button variant="chat-item" className={css.item} onClick={openChat}>
          <span className={css.text}>{title}</span>
          {/* Rendered as a <span> (not the default <button>) so it can nest
              inside the row's <Button> without invalid button-in-button HTML.
              stopPropagation on both mousedown and click keeps it from
              triggering the row's ripple / openChat. */}
          <IconButton
            component="span"
            size="small"
            className={classNames(css.menuButton, menuAnchor && css.menuButtonOpen)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onMenuButtonClick}
            aria-label="Chat options"
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
        </Button>
      </Tooltip>

      {menuAnchor && (
        <Menu
          anchorEl={menuAnchor}
          open
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem
            onClick={() => {
              // Reveal the destination group first so the row doesn't disappear
              // into a collapsed section when it switches partitions.
              onStarToggle?.(!chat.starred);
              chatListStore.toggleStarred(chat._id).catch(console.error);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <StarIcon fontSize="small" color={chat.starred ? 'warning' : 'action'} />
            </ListItemIcon>
            <Typography variant="inherit">{chat.starred ? 'Unstar' : 'Star'} chat</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              renameModalRef.current?.open();
              closeMenu();
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Rename chat</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              chatListStore.cloneChat(chat._id).catch(console.error);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Clone chat</Typography>
          </MenuItem>

          <MenuItem
            disabled={chat.starred}
            onClick={() => {
              deleteModalRef.current?.open();
              closeMenu();
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color={chat.starred ? 'disabled' : 'error'} />
            </ListItemIcon>
            <Typography variant="inherit" color={chat.starred ? undefined : 'error'}>
              Delete chat
            </Typography>
          </MenuItem>
        </Menu>
      )}

      <RenameChatModal
        ref={renameModalRef}
        defaultValue={title}
        onSubmit={(name) => chatListStore.renameChat(chat._id, name)}
      />

      <DeleteChatModal
        ref={deleteModalRef}
        chatTitle={title}
        onConfirm={() => chatListStore.deleteChat(chat._id)}
      />
    </>
  );
});
